import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { context } from 'bookingbug-core-js';

/**
 * Civica eStore PRE-BASKET (book-after-payment) integration for Solihull
 * appointments — talks to bl-civica-api (`/payment/civica/pre-basket*`).
 *
 * Flow (mirrors the working Adelante/HeyCentric pre-basket standard):
 *   1. preBasketInvoke — persists the booking intent to DynamoDB
 *      (bl_purchase_logs_{client}) and builds a Civica eStore basket
 *      (CreateImmediatePaymentBasket). Returns the gateway redirect `url`.
 *   2. The browser pays at the Civica eStore.
 *   3. Civica notifies bl-civica-api server-to-server; the shared
 *      bl-payment-logs post-payment worker checks out the JRNI basket, links
 *      the extra items carried in `data`, and marks the row PAID.
 *   4. preBasketFind — polled by verify-payment on return until the row shows
 *      the JRNI purchase (data.purchase_id / status PAID).
 *
 * Multi-line: the Civica eStore charges a single total; the itemised fees are
 * pulled from the JRNI extra items and passed in `data` so the worker links
 * each extra item to the created booking and recomputes the price server-side.
 */
@Injectable({
  providedIn: 'root',
})
export class CivicaService {
  private url = 'https://staging.bookinglab.co.uk';
  // private url = 'http://localhost:4201';
  private path = '/';
  civicaUrlPath =
    window.location.hostname.indexOf('staging') !== -1
      ? '/payment/'
      : '/payment/';

  constructor(private http: HttpClient) {}

  async preBasketInvoke(
    company,
    service,
    basket,
    serviceItems,
    descriptions,
    client,
    fundCode,
    amounts,
    authToken,
    data,
    slot,
    extraDuration,
    journeyRoute,
    reference,
    parentCompanyId?,
    quantity?,
    catalogueIds?,
    email?,
    phone?
  ) {
    let port = '';
    // '' is the default port for the scheme -- without it a plain https
    // page scored a stray ":" into the payment gateway URL.
    const ports: any = ['80', '443', ''];
    if (ports.indexOf(window.location.port) === -1) {
      port = `:${window.location.port}`;
    }

    let serviceItemString = '';
    for (const serviceItem of serviceItems) {
      serviceItemString = `${serviceItemString},${serviceItem.id}`;
    }

    let returnUrl = '';
    if (journeyRoute == 'order_a_copy_certificate') {
      returnUrl = `${window.location.protocol}//${window.location.hostname}${port}/start/${journeyRoute}/verify/${company.id}/${basket.id}/${serviceItemString}`;
    } else {
      returnUrl = `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify/${company.id}/${basket.id}/${serviceItemString}`;
    }

    console.log(`journeyRoute: ${journeyRoute} Return URL: ${returnUrl}`);

    const body: any = {
      host: `${context.apiUrl}`,
      // company_id MUST be the PARENT (token-holding) company; child_company is
      // the location the booking sits under. The post-payment worker resolves
      // the token holder from parent_company_id first.
      company_id: parentCompanyId === undefined ? `${company.id}` : parentCompanyId,
      child_company: `${company.id}`,
      service_id: `${service.id}`,
      client_id: `${client.id}`,
      basket: `${basket.id}`,
      service_items: serviceItems,
      descriptions: descriptions,
      data: data,
      return_url: `${returnUrl}`,
      back_url: `${returnUrl}`,
      auth_token: `${authToken}`,
      slot: slot,
      extra_duration: extraDuration,
      journey_route: journeyRoute,
      reference: `${reference}`,
    };

    if (email) {
      body.email = `${email}`;
    }
    if (phone) {
      body.phone = `${phone}`;
    }
    if (quantity) {
      body.quantity = quantity;
    }
    if (catalogueIds) {
      body.catalogue_ids = catalogueIds;
    }

    if (Array.isArray(amounts)) {
      body.amounts = amounts;
    } else {
      body.amount = `${amounts}`;
    }
    if (Array.isArray(fundCode)) {
      body.fund_codes = fundCode;
    } else {
      body.fund_code = `${fundCode}`;
    }

    const result: any = await this.http
      .post(`${this.url}${this.civicaUrlPath}civica/pre-basket`, body)
      .toPromise();
    return { ...result, url: result?.url };
  }

  // Poll a pre-basket row back by basket + service item. The row's status
  // (NEW → BOOKING_MADE → PAID / FAILED_*) and data.purchase_id tell the
  // verify page when the backend worker has created the JRNI booking.
  async preBasketFind(companyId, basketId, serviceItemId) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
    };
    return this.http
      .post(`${this.url}${this.civicaUrlPath}civica/pre-basket/find`, body)
      .toPromise();
  }

  async preBasketUpdate(companyId, basketId, serviceItemId, status, ref, data) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
      ref: ref,
      status: `${status}`,
      data: data,
    };
    return this.http
      .post(`${this.url}${this.civicaUrlPath}civica/pre-basket/update`, body)
      .toPromise();
  }
}
